import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import VoiceInput from "@/components/VoiceInput";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

/* Category-specific body-part / view presets */
const PRESETS = {
  "X-RAY": {
    body_parts: [
      "Chest", "Skull", "Cervical Spine", "Thoracic Spine", "Lumbar Spine",
      "Pelvis", "Hip", "Shoulder", "Arm (Humerus)", "Elbow", "Forearm", "Wrist", "Hand", "Fingers",
      "Thigh (Femur)", "Knee", "Leg (Tibia-Fibula)", "Ankle", "Foot", "Heel", "Toes", "KUB", "Abdomen (Erect)", "Abdomen (Supine)",
    ],
    views: ["PA", "AP", "LAT", "AP-LAT", "Oblique", "Standing", "Skyline"],
    show_films: true,
    show_contrast: false,
  },
  "CT": {
    body_parts: [
      "Brain (Plain)", "Brain (Contrast)", "PNS", "Neck", "Chest", "HRCT Chest",
      "Abdomen", "Pelvis", "Whole Abdomen", "KUB", "Spine", "CT Angiography",
    ],
    views: [],
    show_films: false,
    show_contrast: true,
  },
  "MRI": {
    body_parts: [
      "Brain", "Cervical Spine", "Dorsal Spine", "Lumbar Spine", "Whole Spine",
      "Knee", "Shoulder", "Ankle", "Hip", "Wrist", "Abdomen", "Pelvis",
      "MRCP", "MRA Brain", "MR Angiography",
    ],
    views: [],
    show_films: false,
    show_contrast: true,
  },
  "USG": {
    body_parts: [
      "Whole Abdomen", "Upper Abdomen", "Lower Abdomen", "KUB", "Pelvis",
      "Thyroid", "Breast", "Scrotum", "Obstetric (1st Trim)",
      "Obstetric (Anomaly)", "Obstetric (Growth)", "Doppler Carotid",
      "Doppler Lower Limb", "Doppler Upper Limb", "Soft Tissue",
    ],
    views: [],
    show_films: false,
    show_contrast: false,
  },
  "ECHO": {
    body_parts: ["2D Echo", "Stress Echo", "TEE"],
    views: [],
    show_films: false,
    show_contrast: false,
  },
  "LAB": {
    body_parts: [],
    sample_types: ["Blood", "Urine", "Stool", "Serum", "Plasma", "Fluid", "Swab"],
    urgencies: ["Routine", "Urgent", "STAT"],
    views: [],
    show_films: false,
    show_contrast: false,
  },
  "ECG": {
    body_parts: [],
    views: [],
    show_films: false,
    show_contrast: false,
  },
};

const SIDES = [["Left", "Left"], ["Right", "Right"], ["Bilateral", "Both"]];
const SIDEABLE = /hip|shoulder|arm|humerus|elbow|forearm|wrist|hand|finger|thigh|femur|knee|leg|tibia|ankle|foot|heel|toe|breast|limb|clavicle|ear|eye/i;
const splitSide = (s) => {
  const m = /^(Left|Right|Bilateral)\s+(.+)$/.exec(s || "");
  return m ? [m[2], m[1]] : [s, ""];
};

export default function InvestigationDetailDialog({ open, onClose, master, existing, onSave }) {
  const cat = (master?.category || "").toUpperCase();
  const cfg = PRESETS[cat] || { body_parts: [], views: [], show_films: false, show_contrast: false };

  const [bodyParts, setBodyParts] = useState([]);
  const [views, setViews] = useState([]);
  const [contrast, setContrast] = useState("No");
  const [films, setFilms] = useState(1);
  const [sampleType, setSampleType] = useState("");
  const [urgency, setUrgency] = useState("Routine");
  const [fasting, setFasting] = useState(false);
  const [indication, setIndication] = useState("");
  const [notes, setNotes] = useState("");
  const [customPart, setCustomPart] = useState("");
  const [sides, setSides] = useState({});

  useEffect(() => {
    if (!open) return;
    const e = existing || {};
    const raw = Array.isArray(e.body_parts) ? e.body_parts : (e.body_part ? [e.body_part] : []);
    const sd = {};
    setBodyParts(raw.map((s) => { const [p, side] = splitSide(s); if (side) sd[p] = side; return p; }));
    setSides(sd);
    setViews(Array.isArray(e.views) ? e.views : []);
    setContrast(e.contrast || "No");
    setFilms(e.films || 1);
    setSampleType(e.sample_type || (cfg.sample_types?.[0] || ""));
    setUrgency(e.urgency || "Routine");
    setFasting(!!e.fasting);
    setIndication(e.indication || "");
    setNotes(e.notes || "");
    setCustomPart("");
  }, [open, existing, cat]);

  if (!master) return null;

  const toggle = (arr, setArr, v) => setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const submit = () => {
    const missing = bodyParts.filter((p) => SIDEABLE.test(p) && !sides[p]);
    if (missing.length && !window.confirm(`Side (Left/Right) not selected for: ${missing.join(", ")}. Continue anyway?`)) return;
    const labelled = bodyParts.map((p) => (sides[p] ? `${sides[p]} ${p}` : p));
    const payload = {
      body_parts: labelled,
      body_part: labelled.join(", "), // legacy single-string for print compatibility
      views,
      indication,
      notes,
    };
    if (cfg.show_contrast) payload.contrast = contrast;
    if (cfg.show_films) payload.films = Math.max(1, parseInt(films) || 1);
    if (cat === "LAB") {
      payload.sample_type = sampleType;
      payload.urgency = urgency;
      payload.fasting = fasting;
    }
    onSave(payload);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="inv-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="text-xs font-mono text-slate-500">{cat}</span>
            <span>{master.name}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
          {/* Body parts */}
          {cfg.body_parts.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs">Body Part / Region <span className="text-slate-400">(multi-select)</span></Label>
                <span className="text-[10px] text-slate-500">Selected: {bodyParts.length}</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {cfg.body_parts.map((p) => (
                  <button key={p} type="button"
                    data-testid={`inv-part-${p}`}
                    onClick={() => toggle(bodyParts, setBodyParts, p)}
                    className={`px-2.5 py-1 text-[11px] border rounded-sm ${bodyParts.includes(p) ? "border-blue-700 bg-blue-50 text-blue-700 font-semibold" : "border-slate-300 bg-white hover:border-blue-400"}`}>
                    {p}
                  </button>
                ))}
              </div>
              {bodyParts.some((p) => SIDEABLE.test(p)) && (
                <div className="mt-2 border border-amber-200 bg-amber-50/60 rounded-sm p-2 space-y-1.5" data-testid="inv-side-section">
                  <div className="text-[11px] font-semibold text-amber-800">Side (Left / Right)</div>
                  {bodyParts.filter((p) => SIDEABLE.test(p)).map((p) => (
                    <div key={p} className="flex items-center gap-2">
                      <span className="text-[11px] w-36 truncate">{p}</span>
                      {SIDES.map(([val, label]) => (
                        <button key={val} type="button" data-testid={`inv-side-${p}-${val}`}
                          onClick={() => setSides({ ...sides, [p]: sides[p] === val ? "" : val })}
                          className={`px-2.5 py-0.5 text-[11px] border rounded-sm ${sides[p] === val ? "border-amber-600 bg-amber-100 text-amber-900 font-semibold" : "border-slate-300 bg-white"}`}>
                          {label}
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2 mt-2">
                <Input value={customPart} onChange={(e) => setCustomPart(e.target.value)} placeholder="Add custom body part…" className="h-8 text-xs" data-testid="inv-custom-part"/>
                <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => {
                  if (customPart.trim()) {
                    setBodyParts([...bodyParts, customPart.trim()]);
                    setCustomPart("");
                  }
                }}>Add</Button>
              </div>
            </div>
          )}

          {/* Views (X-Ray) */}
          {cfg.views.length > 0 && (
            <div>
              <Label className="text-xs">Views / Projections</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {cfg.views.map((v) => (
                  <button key={v} type="button"
                    data-testid={`inv-view-${v}`}
                    onClick={() => toggle(views, setViews, v)}
                    className={`px-2.5 py-1 text-[11px] border rounded-sm ${views.includes(v) ? "border-teal-600 bg-teal-50 text-teal-700 font-semibold" : "border-slate-300 bg-white"}`}>
                    {v}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Contrast (CT/MRI) */}
          {cfg.show_contrast && (
            <div>
              <Label className="text-xs">Contrast</Label>
              <div className="flex gap-1.5 mt-1">
                {["No", "IV Contrast", "Oral", "IV + Oral", "Gadolinium"].map((c) => (
                  <button key={c} type="button"
                    data-testid={`inv-contrast-${c}`}
                    onClick={() => setContrast(c)}
                    className={`px-2.5 py-1 text-[11px] border rounded-sm ${contrast === c ? "border-purple-600 bg-purple-50 text-purple-700 font-semibold" : "border-slate-300 bg-white"}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Films (X-Ray) */}
          {cfg.show_films && (
            <div className="flex items-center gap-2">
              <Label className="text-xs">Number of Films</Label>
              <Input type="number" min={1} value={films} onChange={(e) => setFilms(e.target.value)}
                className="h-8 w-24" data-testid="inv-films"/>
              <span className="text-[10px] text-slate-500">Bill quantity multiplies with this count</span>
            </div>
          )}

          {/* LAB specifics */}
          {cat === "LAB" && (
            <>
              <div>
                <Label className="text-xs">Sample Type</Label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {(cfg.sample_types || []).map((s) => (
                    <button key={s} type="button"
                      data-testid={`inv-sample-${s}`}
                      onClick={() => setSampleType(s)}
                      className={`px-2.5 py-1 text-[11px] border rounded-sm ${sampleType === s ? "border-blue-700 bg-blue-50 text-blue-700 font-semibold" : "border-slate-300 bg-white"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div>
                  <Label className="text-xs">Urgency</Label>
                  <div className="flex gap-1.5 mt-1">
                    {(cfg.urgencies || []).map((u) => (
                      <button key={u} type="button"
                        data-testid={`inv-urgency-${u}`}
                        onClick={() => setUrgency(u)}
                        className={`px-2.5 py-1 text-[11px] border rounded-sm ${urgency === u ? (u === "STAT" ? "border-red-600 bg-red-50 text-red-700 font-semibold" : "border-teal-600 bg-teal-50 text-teal-700 font-semibold") : "border-slate-300 bg-white"}`}>
                        {u}
                      </button>
                    ))}
                  </div>
                </div>
                <label className="flex items-center gap-2 text-xs mt-5">
                  <input type="checkbox" checked={fasting} onChange={(e) => setFasting(e.target.checked)} data-testid="inv-fasting"/>
                  Fasting sample
                </label>
              </div>
            </>
          )}

          {/* Clinical indication - common */}
          <div>
            <Label className="text-xs">Clinical Indication</Label>
            <VoiceInput rows={2} value={indication} onChange={setIndication}
              placeholder="e.g. Suspected pneumonia, R/O fracture, USG A/W pain…"
              testid="inv-indication"/>
          </div>

          {/* Notes */}
          <div>
            <Label className="text-xs">Additional Notes <span className="text-slate-400">(optional)</span></Label>
            <VoiceInput rows={2} value={notes} onChange={setNotes}
              placeholder="Prep instructions, allergies, prior reports…"
              testid="inv-notes"/>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="inv-detail-cancel">Cancel</Button>
          <Button className="bg-blue-700 hover:bg-blue-800" onClick={submit} data-testid="inv-detail-save">
            {existing ? "Update Detail" : "Add Investigation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
